import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { ItemCard, PressableFeedback } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    TINT_CHIP_CLASS,
    pinnedDetail,
    type ControlAction,
} from '../../controlCenterMeta';
import type { ActionRefusals } from '../../useControlCenterPage';
import { ActionRefusalPopover } from '../actionRefusal';
import { usePinnedDrag } from './usePinnedDrag';

type PinnedActionsProps = {
    actions: ControlAction[];
    refusals: ActionRefusals;
    isEditing: boolean;
    onToggleEditing: () => void;
    onRun: (action: ControlAction) => void;
    onUnpin: (id: string) => void;
    onReorder: (sourceId: string, targetId: string) => void;
    onMove: (id: string, delta: number) => void;
};

type PinnedTileProps = {
    action: ControlAction;
    refusals: ActionRefusals;
    onRun: (action: ControlAction) => void;
};

type PinnedEditRowProps = {
    action: ControlAction;
    onUnpin: (id: string) => void;
    onReorder: (sourceId: string, targetId: string) => void;
    onMove: (id: string, delta: number) => void;
};

/**
 * The six-or-so things this reader keeps at hand.
 *
 * The only place on the page that carries colour: the tile's `ItemCard.Icon`
 * takes a tint from three semantic values rather than one hue per action.
 * Colour marks what was chosen, and thirty-six tints would mark nothing.
 *
 * There is **no cap**. The grid flows, each new pin landing in the next free
 * cell, so pinning never has to refuse — which is what lets the catalog below
 * double as the pin manager.
 */
export const PinnedActions: React.FC<PinnedActionsProps> = ({
    actions,
    refusals,
    isEditing,
    onToggleEditing,
    onRun,
    onUnpin,
    onReorder,
    onMove,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('controlCenter.pinned.title')}
            className="flex flex-col gap-3"
            {...getNodeTestIdProp('control-center-pinned')}
        >
            <div className="flex items-center gap-3">
                <h2 className="text-[15px] font-medium tracking-tight text-foreground">
                    {t('controlCenter.pinned.title')}
                </h2>
                <span className="text-xs text-muted">
                    {isEditing
                        ? t('controlCenter.pinned.reorderHint', {
                              count: actions.length,
                          })
                        : t('controlCenter.pinned.count', {
                              count: actions.length,
                          })}
                </span>
                <div className="flex-1" />
                <Button
                    onPress={onToggleEditing}
                    size="sm"
                    variant={isEditing ? 'primary' : 'tertiary'}
                    {...getNodeTestIdProp('control-center-pinned-edit-toggle')}
                >
                    {isEditing ? null : (
                        <Icon aria-hidden icon="solar:tuning-2-linear" />
                    )}
                    {isEditing
                        ? t('controlCenter.pinned.done')
                        : t('controlCenter.pinned.edit')}
                </Button>
            </div>

            {actions.length === 0 ? (
                <p
                    className="rounded-2xl border border-dashed border-separator px-4 py-6 text-center text-sm text-muted"
                    {...getNodeTestIdProp('control-center-pinned-empty')}
                >
                    {t('controlCenter.pinned.empty')}
                </p>
            ) : (
                <ul className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                    {actions.map((action) => (
                        <li className="flex" key={action.id}>
                            {isEditing ? (
                                <PinnedEditRow
                                    action={action}
                                    onMove={onMove}
                                    onReorder={onReorder}
                                    onUnpin={onUnpin}
                                />
                            ) : (
                                <PinnedTile
                                    action={action}
                                    onRun={onRun}
                                    refusals={refusals}
                                />
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

const PinnedTile: React.FC<PinnedTileProps> = ({ action, refusals, onRun }) => {
    const { t } = useTranslation('pages');
    const detail = pinnedDetail(action);
    const refusal = refusals.refusalFor(action.id);

    const body = (
        <>
            <ItemCard.Icon className={TINT_CHIP_CLASS[action.tint]}>
                <Icon aria-hidden className="size-[18px]" icon={action.icon} />
            </ItemCard.Icon>
            <ItemCard.Content className="min-w-0">
                <ItemCard.Title>
                    {t(`controlCenter.actions.${action.id}`)}
                </ItemCard.Title>
                <ItemCard.Description>
                    {t(detail.key, detail.params)}
                </ItemCard.Description>
            </ItemCard.Content>
            {action.opensOwnPage ? (
                <ItemCard.Action>
                    <Icon
                        aria-hidden
                        className="size-4 text-muted"
                        icon="solar:arrow-right-up-linear"
                    />
                </ItemCard.Action>
            ) : null}
        </>
    );

    if (refusal) {
        return (
            <ActionRefusalPopover
                action={action}
                className="w-full"
                refusal={refusal}
                trigger="wrap"
            >
                {/* A plain card, not a button: `Popover.Trigger` is the button,
                    and nesting one inside another is invalid markup. */}
                <ItemCard
                    className="w-full cursor-pointer"
                    {...getNodeTestIdProp(
                        `control-center-pinned-tile-${action.id}`,
                    )}
                >
                    {body}
                </ItemCard>
            </ActionRefusalPopover>
        );
    }

    return (
        <ItemCard<'button'>
            className="relative w-full cursor-pointer overflow-hidden"
            onClick={() => onRun(action)}
            render={(props) => <button type="button" {...props} />}
            {...getNodeTestIdProp(`control-center-pinned-tile-${action.id}`)}
        >
            <PressableFeedback.Highlight />
            {body}
        </ItemCard>
    );
};

/**
 * A pin in edit mode: grab handle, name, remove.
 *
 * The card is **not** pressable here — in edit mode a row is a thing you
 * rearrange rather than a thing you run, and a card that both moves and fires
 * would mean every drag risks opening a modal.
 */
const PinnedEditRow: React.FC<PinnedEditRowProps> = ({
    action,
    onUnpin,
    onReorder,
    onMove,
}) => {
    const { t } = useTranslation('pages');
    const label = t(`controlCenter.actions.${action.id}`);
    const drag = usePinnedDrag({ id: action.id, onReorder, onMove });

    return (
        <ItemCard
            className="w-full"
            draggable
            onDragOver={drag.onDragOver}
            onDragStart={drag.onDragStart}
            onDrop={drag.onDrop}
            {...getNodeTestIdProp(`control-center-pinned-row-${action.id}`)}
        >
            <Button
                aria-label={t('controlCenter.pinned.reorder', {
                    action: label,
                })}
                className="cursor-grab"
                isIconOnly
                onKeyDown={drag.onHandleKeyDown}
                size="sm"
                variant="ghost"
                {...getNodeTestIdProp(
                    `control-center-pinned-handle-${action.id}`,
                )}
            >
                <Icon aria-hidden icon="solar:hamburger-menu-linear" />
            </Button>
            <ItemCard.Icon className={TINT_CHIP_CLASS[action.tint]}>
                <Icon aria-hidden className="size-4" icon={action.icon} />
            </ItemCard.Icon>
            <ItemCard.Content className="min-w-0">
                <ItemCard.Title>{label}</ItemCard.Title>
            </ItemCard.Content>
            <ItemCard.Action>
                <Button
                    aria-label={t('controlCenter.pinned.unpin', {
                        action: label,
                    })}
                    isIconOnly
                    onPress={() => onUnpin(action.id)}
                    size="sm"
                    variant="danger-soft"
                    {...getNodeTestIdProp(
                        `control-center-pinned-remove-${action.id}`,
                    )}
                >
                    <Icon aria-hidden icon="solar:minus-circle-linear" />
                </Button>
            </ItemCard.Action>
        </ItemCard>
    );
};
